import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import SEO from '../components/SEO';
import ImageGallery from '../components/ImageGallery';
import useIsMobile from '../hooks/useIsMobile';

interface Review {
  id: string;
  name: string;
  title?: string;
  facility: string;
  rating: number;
  body: string;
}

const customers = [
  { name: 'Kaiser Permanente', location: 'Oakland, CA', type: 'Hospital System', site: 'https://healthy.kaiserpermanente.org/', img: '/images/logos/kaiser-7e4ffa2a.png' },
  { name: 'The University of Kansas Health System', location: 'Kansas City, KS', type: 'Academic Medical Center', site: 'https://www.kansashealthsystem.com/', img: '/images/logos/kansas-bde6b540.png' },
  { name: 'Rutgers School of Dental Medicine', location: 'Newark, NJ', type: 'Dental School', site: 'https://sdm.rutgers.edu/', img: '/images/logos/rutgers-9ad8b033.png' },
  { name: 'Michigan Medicine', location: 'Ann Arbor, MI', type: 'Academic Medical Center', site: 'https://www.uofmhealth.org/', img: '/images/logos/umich-ba45b599.png' },
  { name: 'OHSU', location: 'Portland, OR', type: 'Academic Medical Center', site: 'https://www.ohsu.edu/', img: '/images/logos/ohsu-43eee7dd.png' },
  { name: 'Endeavor Health', location: 'Evanston, IL', type: 'Health System', site: 'https://www.endeavorhealth.org', img: '/images/logos/endeavor-6025451a.png' },
  { name: 'Hartford HealthCare', location: 'Hartford, CT', type: 'Health System', site: 'https://hartfordhealthcare.org', img: '/images/logos/hartford-ec32c536.png' },
  { name: 'Nebraska Medicine', location: 'Omaha, NE', type: 'Academic Medical Center', site: 'https://www.nebraskamed.com', img: '/images/logos/nebraska-c56a568e.png' },
  { name: "Arkansas Children's", location: 'Little Rock, AR', type: 'Pediatric Hospital', site: 'https://www.archildrens.org', img: '/images/logos/arkansas-e483338c.png' },
];


const OurWorkPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'gallery' | 'reviews' | 'customers' | 'news'>('gallery');
  const [galleryCategory, setGalleryCategory] = useState<string | null>(null);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [visibleCount, setVisibleCount] = useState(6);
  const location = useLocation();
  const isMobile = useIsMobile();

  useEffect(() => {
    const state = location.state as any;
    if (state?.category) { setActiveTab('gallery'); setGalleryCategory(state.category); }
    else if (state?.tab) { setActiveTab(state.tab); }
  }, [location.state]);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  useEffect(() => {
    fetch('/reviews/approved.json')
      .then(r => r.ok ? r.json() : [])
      .then((data: Review[]) => setReviews(data))
      .catch(() => setReviews([]));
  }, []);

  return (
    <>
      <SEO
        title="Our Work | Projects, Reviews & Customers"
        description="Browse JVP Health Tech Solutions' project gallery, read reviews from healthcare professionals, and see the hospitals and health systems we've served nationwide."
        keywords="healthcare equipment installation gallery, hospital installation projects, JVP reviews, healthcare facility customers, medical equipment installation photos"
        url="https://www.jvphealthtechsolutions.com/work"
      />
      <div className="page" style={{ paddingTop: '80px' }}>

        {/* Hero */}
        {!(activeTab === 'gallery' && galleryCategory) && (
          <div style={{ background: 'linear-gradient(135deg, var(--color-navy) 0%, var(--color-primary) 100%)', color: 'white', padding: isMobile ? '3rem 1.25rem' : '5rem 2rem', textAlign: 'center' }}>
            <h1 style={{ color: 'white', fontSize: isMobile ? '2rem' : '2.8rem', marginBottom: '1rem' }}>Our Work</h1>
            <p style={{ fontSize: '1.2rem', maxWidth: '600px', margin: '0 auto', opacity: 0.9, lineHeight: 1.7 }}>
              Real facilities. Real results. See how we've helped healthcare teams across the nation work smarter and safer.
            </p>
          </div>
        )}

        {/* Tabs */}
        <div data-gallery-tabs className="tab-bar" style={{ background: 'white', borderBottom: '1px solid var(--color-border)', position: 'sticky', zIndex: 10 }}>
          <div style={{ maxWidth: '1100px', margin: '0 auto', display: 'flex', gap: '0', padding: isMobile ? '0 0.75rem' : '0 2rem' }}>
            {(['gallery', 'reviews', 'customers', 'news'] as const).map(tab => (
              <button
                key={tab}
                onClick={() => { setActiveTab(tab); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                style={{
                  padding: isMobile ? '1rem 0.9rem' : '1.1rem 1.75rem',
                  background: 'none',
                  border: 'none',
                  borderBottom: activeTab === tab ? '3px solid var(--color-primary)' : '3px solid transparent',
                  color: activeTab === tab ? 'var(--color-primary)' : 'var(--color-text-faint)',
                  fontWeight: activeTab === tab ? 700 : 500,
                  fontSize: '0.95rem',
                  cursor: 'pointer',
                  textTransform: 'capitalize',
                  transition: 'all 0.2s',
                }}
              >
                {tab === 'news' ? 'In the News' : tab === 'customers' ? 'Customers' : tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Gallery */}
        {activeTab === 'gallery' && (
          <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '4rem 2rem' }}>
            {!galleryCategory && (
              <>
                <h2 style={{ color: 'var(--color-text)', fontSize: '1.75rem', marginBottom: '0.5rem' }}>Project Gallery</h2>
                <p style={{ color: 'var(--color-text-muted)', marginBottom: '2.5rem' }}>A look at installations and deployments across our customer facilities.</p>
              </>
            )}
            <ImageGallery activeCategory={galleryCategory} onCategoryChange={setGalleryCategory} />
          </div>
        )}

        {/* Reviews */}
        {activeTab === 'reviews' && (
          <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '4rem 2rem' }}>
            <h2 style={{ color: 'var(--color-text)', fontSize: '1.75rem', marginBottom: '0.5rem' }}>What Our Customers Say</h2>
            <p style={{ color: 'var(--color-text-muted)', marginBottom: '2.5rem' }}>Feedback from the healthcare professionals we've had the privilege of working with.</p>
            {reviews.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '4rem 1rem', background: 'var(--color-surface)', borderRadius: '14px', border: '1px solid var(--color-border)' }}>
                <p style={{ color: 'var(--color-text-label)', fontSize: '1.1rem', marginBottom: '1.5rem' }}>No reviews yet — be the first to share your experience!</p>
                <a href="/reviews" style={{ background: 'var(--color-primary)', color: 'white', padding: '0.75rem 2rem', borderRadius: '50px', fontWeight: 600, textDecoration: 'none', fontSize: '0.95rem' }}>Leave a Review →</a>
              </div>
            ) : (
              <>
                <div className="ow-reviews-grid">
                  {reviews.slice(0, visibleCount).map(r => (
                    <div key={r.id} className="ow-review-card">
                      <div style={{ color: 'var(--color-warning)', fontSize: '1.1rem', marginBottom: '1rem' }}>
                        {'★'.repeat(r.rating)}
                      </div>
                      <p style={{ color: 'var(--color-text-label)', lineHeight: 1.7, fontStyle: 'italic', marginBottom: '1.5rem' }}>"{r.body}"</p>
                      <div>
                        <strong style={{ color: 'var(--color-text)', display: 'block' }}>{r.name}</strong>
                        <span style={{ color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>
                          {r.title ? `${r.title} · ` : ''}{r.facility}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{ textAlign: 'center', marginTop: '2.5rem' }}>
                  {visibleCount < reviews.length && (
                    <button onClick={() => setVisibleCount(c => c + 6)} style={{ background: 'white', color: 'var(--color-primary)', border: '2px solid var(--color-primary)', padding: '0.75rem 2rem', borderRadius: '50px', fontWeight: 600, fontSize: '0.95rem', cursor: 'pointer', marginBottom: '1.5rem' }}>
                      Load More Reviews
                    </button>
                  )}
                  <p style={{ color: 'var(--color-text-muted)', marginBottom: '1rem' }}>Had a great experience? Share it with others.</p>
                  <a href="/reviews" style={{ background: 'var(--color-primary)', color: 'white', padding: '0.75rem 2rem', borderRadius: '50px', fontWeight: 600, textDecoration: 'none', fontSize: '0.95rem' }}>Leave a Review →</a>
                </div>
              </>
            )}
          </div>
        )}

        {/* Customers */}
        {activeTab === 'customers' && (
          <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '4rem 2rem' }}>
            <h2 style={{ color: 'var(--color-text)', fontSize: '1.75rem', marginBottom: '0.5rem' }}>Trusted Across Healthcare</h2>
            <p style={{ color: 'var(--color-text-muted)', marginBottom: '2.5rem' }}>We're proud to support some of the nation's top hospitals and health systems.</p>

            <div className="ow-customers-grid" style={{ marginBottom: '2.5rem' }}>
              {customers.map((c, i) => (
                <a key={i} href={c.site} target="_blank" rel="noopener noreferrer" className="ow-logo-card">
                  <div className="ow-logo-img-wrap">
                    <picture>
                      <source srcSet={c.img.replace(/\.[^.]+$/, '.avif')} type="image/avif" />
                      <img src={c.img} alt={c.name} className="ow-logo-img" />
                    </picture>
                  </div>
                </a>
              ))}
            </div>

          </div>
        )}

        {/* News */}
        {activeTab === 'news' && (
          <div style={{ maxWidth: '900px', margin: '0 auto', padding: '4rem 2rem' }}>
            <h2 style={{ color: 'var(--color-text)', fontSize: '1.75rem', marginBottom: '0.5rem' }}>In the News</h2>
            <p style={{ color: 'var(--color-text-muted)', marginBottom: '2.5rem' }}>Media coverage featuring JVP Health Tech Solutions and the facilities we serve.</p>

            <div className="news-card">
              <a
                href="https://www.ksla.com/video/2026/03/19/upgrades-lsu-health-shreveports-anatomical-sciences-wing-advances-learning-tools-students/"
                target="_blank"
                rel="noopener noreferrer"
                className="news-card-thumb"
              >
                <picture>
                  <source srcSet="/images/news/ksla-lsu-health.avif" type="image/avif" />
                  <img
                    src="/images/news/ksla-lsu-health.jpg"
                    alt="KSLA News: Upgrades to LSU Health Shreveport's Anatomical Sciences Wing"
                  />
                </picture>
                <div className="news-play-btn" aria-hidden="true">▶</div>
              </a>
              <div className="news-card-body">
                <div className="news-card-meta">
                  <span className="news-source-badge">KSLA News 12</span>
                  <span style={{ color: 'var(--color-text-muted)', fontSize: '0.85rem' }}>March 19, 2026</span>
                </div>
                <h3 className="news-card-headline">
                  Upgrades to LSU Health Shreveport's Anatomical Sciences Wing Advance Learning Tools for Students
                </h3>
                <p className="news-card-desc">
                  KSLA News 12 covers the latest technology upgrades at LSU Health Shreveport's Anatomical Sciences Wing,
                  highlighting how new tools are transforming the learning environment for medical students.
                </p>
                <a
                  href="https://www.ksla.com/video/2026/03/19/upgrades-lsu-health-shreveports-anatomical-sciences-wing-advances-learning-tools-students/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="news-card-link"
                >
                  Watch Full Story →
                </a>
              </div>
            </div>
          </div>
        )}

        {/* CTA */}
        <div style={{ background: 'linear-gradient(135deg, var(--color-navy) 0%, var(--color-primary) 100%)', color: 'white', padding: '5rem 2rem', textAlign: 'center' }}>
          <h2 style={{ color: 'white', fontSize: '2.2rem', marginBottom: '1rem' }}>Ready to Be Our Next Success Story?</h2>
          <p style={{ opacity: 0.9, fontSize: '1.1rem', maxWidth: '500px', margin: '0 auto 2rem' }}>
            Let's talk about your facility's needs and how we can help.
          </p>
          <Link to="/contact" style={{ background: 'white', color: 'var(--color-primary)', padding: '1rem 2.5rem', borderRadius: '50px', fontWeight: 700, textDecoration: 'none', fontSize: '1rem', display: 'inline-block' }}>
            Get in Touch
          </Link>
        </div>

      </div>
    </>
  );
};

export default OurWorkPage;
